import { useCallback } from "react";

import { Stack } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import { AccessResult, NO_ACCESS_MESSAGE } from "utils/access";

import useHasStackWriteAccess from "./useHasStackWriteAccess";
import useCanManageStack from "./useCanManageStack";

const getUnlockStackErrorMessage = (
  isLockedByMe: boolean,
  hasStackWriteAccess: boolean,
  canManageStack: boolean
) => {
  if (!isLockedByMe && !canManageStack && hasStackWriteAccess) {
    return "You can't unlock a stack locked by another user";
  }

  return NO_ACCESS_MESSAGE;
};

/**
 * Hook that returns a function to check if the current user can unlock a stack.
 * A user needs write access to unlock a stack they locked.
 * A user with manage access can unlock any locked stack.
 * A user can't unlock a stack that isn't locked.
 * @returns {(stack: Stack) => AccessResult} Function that takes a stack and returns an AccessResult indicating if user can unlock it
 */
const useCanUnlockStack = (): ((stack: Stack) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);

  const canManageStackFn = useCanManageStack();
  const hasStackWriteAccessFn = useHasStackWriteAccess();

  return useCallback(
    (stack: Stack) => {
      const isLocked = !!stack.lockedAt && !!stack.lockedBy;

      if (!isLocked) {
        return {
          result: false,
        };
      }

      const isLockedByMe = viewer.id === stack.lockedBy;
      const hasStackWriteAccess = hasStackWriteAccessFn(stack);
      const canManageStack = canManageStackFn(stack);
      const hasPermissionToUnlock = canManageStack || (isLockedByMe && hasStackWriteAccess);

      if (!hasPermissionToUnlock) {
        return {
          result: false,
          message: getUnlockStackErrorMessage(isLockedByMe, hasStackWriteAccess, canManageStack),
        };
      }

      return {
        result: true,
      };
    },
    [canManageStackFn, hasStackWriteAccessFn, viewer?.id]
  );
};

export default useCanUnlockStack;
